import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Sym from './Sym';

/**
 * Modal común del rediseño (encabezado + cuerpo + pie de acciones).
 *
 * - Se monta en <body> con createPortal dentro de un wrapper `.as2-portal`, que
 *   es quien lleva los tokens de color (fuera de `.as2` no existirían).
 * - Cierra con Escape y con clic en el fondo; bloquea el scroll del documento.
 * - Devuelve el foco al elemento que lo abrió.
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {string} title      título (obligatorio para accesibilidad)
 * @param {string} subtitle
 * @param {node} footer       acciones del pie
 * @param {number} maxWidth   ancho máximo en px (default 460)
 * @param {boolean} bodyFlush cuerpo sin padding (para visores/embebidos)
 */
const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  footer,
  maxWidth = 460,
  bodyFlush = false,
  children,
}) => {
  const openerRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    openerRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);

    // El panel recibe el foco para que el lector de pantalla anuncie el diálogo.
    const t = setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="as2-portal">
      <div className="as2-modal-overlay" onClick={onClose}>
        <div
          ref={panelRef}
          className="as2-modal"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          style={{ maxWidth }}
          onClick={(e) => e.stopPropagation()}
        >
          {title ? (
            <div className="as2-modal-head">
              <div style={{ minWidth: 0 }}>
                <div style={{ font: "700 1.05rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{title}</div>
                {subtitle ? (
                  <div style={{ font: "500 .8rem 'IBM Plex Mono'", color: 'var(--muted)', marginTop: 2 }}>
                    {subtitle}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                style={{
                  width: 34, height: 34, borderRadius: 9, border: 'none', background: 'var(--surface-2)',
                  color: 'var(--muted)', cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0,
                }}
              >
                <Sym name="close" size={20} />
              </button>
            </div>
          ) : null}

          <div className="as2-modal-body" style={bodyFlush ? { padding: 0 } : undefined}>
            {children}
          </div>

          {footer ? <div className="as2-modal-foot">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
